import * as React from "react";
import { Checkbox as BaseCheckbox, Toggle } from "react-ui";
import { useEffect } from "react";

import { noop } from "../../../../../Common/TypingUtils";
import { useScrollTo } from "../../Internals/Contexts/ScrollToContext";
import { Tooltip } from "../../Internals/Tooltip/Tooltip";
import { FocusManagementObserver } from "../../Services/FocusManagement/FocusManagementObserver";
import { useTestPageObject } from "../../Services/TestPageObjects/PageObjectExposure";
import { emptyRef } from "../../TypingUtils";
import { AbsoluteModelPath } from "../../../../../Common/ModelPath/ModelPath";
import { ControlValidationProps } from "../../Validations/ValidationProps";
import { ControlTooltipProps } from "../../Internals/Tooltip/ControlTooltipProps";
import { useMetrics } from "../../Internals/Metrics/MetricsProviderContext";
import { CHECKBOX } from "../../metricsAction";

import { CheckboxTestPageObject } from "./CheckboxTestPageObject";

export type CheckboxValue = string | boolean;

export interface CheckboxProps extends ControlValidationProps, ControlTooltipProps {
    bindingPath: AbsoluteModelPath;
    checkedValue?: CheckboxValue;
    uncheckedValue?: CheckboxValue;
    value: CheckboxValue;
    children?: React.ReactNode;
    disabled?: boolean;
    kind?: "checkbox" | "toggle";
    isFormReloading?: boolean;
    setError?: (isError: boolean) => void;
    onChange: (value: undefined | CheckboxValue) => void;
    setCorrectValue: (value: undefined | CheckboxValue) => void;
    isReadOnly?: boolean;
    "data-tid"?: string;
}

export function Checkbox(props: CheckboxProps): JSX.Element {
    const track = useMetrics();
    const scrollTo = useScrollTo();
    const container = React.useRef<HTMLDivElement>(emptyRef);
    const baseCheckbox = React.useRef<BaseCheckbox>(emptyRef);
    const baseToggle = React.useRef<Toggle>(emptyRef);

    const {
        value,
        onChange,
        setCorrectValue,
        error,
        warning,
        checkedValue = true,
        uncheckedValue = false,
        setError = noop,
    } = props;

    const handleChange = (checked?: boolean) => {
        let nextValue = checkedValue;
        if (checked !== undefined) {
            nextValue = checked ? checkedValue : uncheckedValue;
        } else if (value === checkedValue || value !== uncheckedValue) {
            nextValue = uncheckedValue;
        }
        if (error) {
            setError(false);
        }
        onChange(nextValue);
        track(CHECKBOX, {
            path: props.bindingPath.toString(),
            isChecked: nextValue === checkedValue ? true : false,
        });
    };

    // -- для кейсов вставки из excel, когда в ячейке отличное от ожидаемого значения - проставим unchecked
    useEffect(() => {
        if (props.isFormReloading) {
            return;
        }
        if (!props.disabled && value !== checkedValue && value !== uncheckedValue) {
            setCorrectValue(uncheckedValue);
        }
    }, [value, props.disabled]);

    const handleRequestFocus = () => {
        if (container.current != undefined) {
            scrollTo(container.current);
        }
        baseCheckbox.current?.focus();
        baseToggle.current?.focus();
    };

    useTestPageObject(
        () => CheckboxTestPageObject(value, checkedValue, handleChange),
        [value, checkedValue, handleChange]
    );

    const tooltipText = error ?? warning ?? props.tooltip ?? "";

    return (
        <FocusManagementObserver onRequestFocus={handleRequestFocus}>
            {props.isReadOnly ? (
                props.kind === "toggle" ? (
                    <Toggle
                        ref={baseToggle}
                        checked={props.value === checkedValue}
                        disabled={props.disabled}
                        error={Boolean(error)}
                        warning={Boolean(warning)}
                        data-tid={props["data-tid"]}
                        onValueChange={handleChange}>
                        {props.children}
                    </Toggle>
                ) : (
                    <BaseCheckbox
                        ref={baseCheckbox}
                        checked={props.value === checkedValue}
                        disabled={true}
                        data-tid={props["data-tid"]}>
                        {props.children}
                    </BaseCheckbox>
                )
            ) : (
                <Tooltip
                    data-tid={"Tooltip"}
                    useWrapper={false}
                    pos={props.tooltipPosition ?? "top left"}
                    trigger={tooltipText ? "hover&focus" : "closed"}
                    value={tooltipText}
                    width={props.tooltipWidth}>
                    <span
                        onClick={e => {
                            e.stopPropagation();
                        }}>
                        {props.kind === "toggle" ? (
                            <Toggle
                                ref={baseToggle}
                                checked={props.value === checkedValue}
                                disabled={props.disabled}
                                error={Boolean(error)}
                                warning={Boolean(warning)}
                                data-tid={props["data-tid"]}
                                onValueChange={handleChange}>
                                {props.children}
                            </Toggle>
                        ) : (
                            <BaseCheckbox
                                ref={baseCheckbox}
                                checked={props.value === checkedValue}
                                disabled={props.disabled}
                                error={Boolean(error)}
                                warning={Boolean(warning)}
                                data-tid={props["data-tid"]}
                                onValueChange={handleChange}>
                                {props.children}
                            </BaseCheckbox>
                        )}
                    </span>
                </Tooltip>
            )}
        </FocusManagementObserver>
    );
}
